import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

/**
 * R&D-E Gap 1 — Firm Risk panel.
 *
 * Read-only firm-wide risk aggregation: band distribution, headline
 * metrics, and the High-Risk Register. Auth is enforced server-side
 * (MLRO / director / COLP / admin); non-eligible users see an error.
 */
const BANDS = ["low", "medium", "high", "prohibited"];

export default function FirmRiskPanel({ authHeaders }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const headers = useCallback(() => {
    if (typeof authHeaders === "function") return authHeaders();
    const email =
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") ||
      "";
    return { "X-Bonsai-User-Id": email };
  }, [authHeaders]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/compliance/firm-risk-summary`, {
        headers: headers(),
      });
      setSummary(res.data || {});
    } catch (err) {
      setError(errorMessage(err, "request failed"));
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const dist = summary?.distribution || {};
  const cov = summary?.thirdfort_coverage || {};
  const register = Array.isArray(summary?.high_risk_register)
    ? summary.high_risk_register
    : [];

  return (
    <section
      data-testid="firm-risk-panel"
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-100">Firm Risk</h2>
        <button
          type="button"
          onClick={fetchSummary}
          disabled={loading}
          data-testid="firm-risk-refresh"
          className="px-2.5 py-1 rounded text-xs font-medium border bg-slate-700 text-slate-100 border-slate-600 hover:bg-slate-600 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <p className="text-xs text-slate-400">
        Firm-wide risk aggregation across the High-Risk Register, screening
        cadence, and Thirdfort artefact coverage. Read-only.
      </p>

      {error ? (
        <div className="rounded-md border border-red-800/50 bg-red-950/30 p-3 text-sm text-red-200">
          <span data-testid="firm-risk-error">{error}</span>{" "}
          <button
            type="button"
            onClick={fetchSummary}
            className="ml-2 px-2 py-0.5 rounded border border-red-700/60 hover:bg-red-900/30"
          >
            Retry
          </button>
        </div>
      ) : loading && !summary ? (
        <div
          data-testid="firm-risk-skeleton"
          className="h-4 w-44 bg-slate-800 rounded animate-pulse"
        />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="firm-risk-distribution">
            {BANDS.map((b) => (
              <Stat key={b} label={b} value={dist[b] ?? 0} />
            ))}
            <Stat label="total" value={dist.total ?? 0} />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Overdue reviews" value={summary?.overdue_review_count ?? 0} />
            <Stat label="Sanctions/PEP 30d" value={summary?.sanctions_pep_signals_30d ?? 0} />
            <Stat label="SAR/internal 30d" value={summary?.sar_internal_reports_30d ?? 0} />
            <Stat
              label="Thirdfort PDF / tick-only"
              value={`${cov.with_pdf ?? 0} / ${cov.tick_only ?? 0}`}
            />
          </div>

          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mt-1 mb-1">
              High-Risk Register
            </h3>
            {register.length === 0 ? (
              <div className="text-sm text-slate-400" data-testid="firm-risk-register-empty">
                No matters currently on the High-Risk Register.
              </div>
            ) : (
              <table className="w-full text-sm text-slate-200" data-testid="firm-risk-register">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-400">
                    <th className="text-left py-1 pr-3">Matter</th>
                    <th className="text-left py-1 pr-3">FE</th>
                    <th className="text-left py-1 pr-3">Override</th>
                    <th className="text-left py-1 pr-3">MLRO notified</th>
                    <th className="text-right py-1 pr-3">Days</th>
                    <th className="text-left py-1">Mid-Point due</th>
                  </tr>
                </thead>
                <tbody>
                  {register.map((r) => (
                    <tr
                      key={r.matter_reference}
                      data-testid="firm-risk-register-row"
                      className="border-t border-slate-800/60"
                    >
                      <td className="py-1 pr-3 font-mono text-xs">{r.matter_reference}</td>
                      <td className="py-1 pr-3">{r.fee_earner || "—"}</td>
                      <td className="py-1 pr-3">{r.supervisor_override_recorded ? "Yes" : "No"}</td>
                      <td className="py-1 pr-3">
                        {r.mlro_notified ? (
                          <span className="text-emerald-300">Yes</span>
                        ) : (
                          <span className="text-amber-300">No</span>
                        )}
                      </td>
                      <td className="py-1 pr-3 text-right font-mono text-xs">
                        {r.days_on_register ?? 0}
                      </td>
                      <td className="py-1">{r.midpoint_due_date || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {Array.isArray(cov.tick_only_high_risk) && cov.tick_only_high_risk.length > 0 && (
            <div
              className="rounded-md border border-amber-800/40 bg-amber-950/20 p-2 text-xs text-amber-200"
              data-testid="firm-risk-tick-only"
            >
              Tick-only high-risk matters (Thirdfort PDF retro-attach needed):{" "}
              {cov.tick_only_high_risk.join(", ")}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded border border-slate-800/60 bg-slate-800/30 px-2 py-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 capitalize">
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-100">{value}</div>
    </div>
  );
}
